import React, { useEffect, useMemo, useState } from 'react';
import {
  PieChart, Pie, Tooltip, Cell, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { IoChevronBack, IoHome } from 'react-icons/io5';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { defaultColor } from '../../../utils/utils';
import styles from './HierarchicalDonut.module.scss';

const keyOf = (node) => node?.elementId || node?.id || node?.name;

const formatValue = (value, unit) => `${unit === '%' ? value?.toFixed(2) : value}${unit || ''}`;

/**
 * Donut of a hierarchical list : only the roots of the list are displayed,
 * a click on a slice replaces the data by the distribution of its children,
 * down to the leaves.
 * Data is the flat list of the nodes of the tree built by buildHierarchicalData.
 */
export default function HierarchicalDonut({ data = [], isLabel }) {
  const { t } = useTranslation();
  // keys of the ancestors of the displayed level, from the root
  const [path, setPath] = useState([]);
  // index of the hovered slice, to only show a pointer on the ones that can be opened
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const { byKey, childrenByKey, roots } = useMemo(() => {
    const nodesByKey = {};
    const nodesByName = {};
    data.forEach((d) => {
      nodesByKey[keyOf(d)] = d;
      if (d.name !== undefined && nodesByName[d.name] === undefined) nodesByName[d.name] = d;
    });
    const parentOf = (node) => (node.parentId && nodesByKey[node.parentId])
      || (node.parentName && nodesByName[node.parentName])
      || null;
    const children = {};
    const rootNodes = [];
    data.forEach((d) => {
      const parent = parentOf(d);
      if (!parent || parent === d) {
        rootNodes.push(d);
        return;
      }
      const parentKey = keyOf(parent);
      if (!children[parentKey]) children[parentKey] = [];
      children[parentKey].push(d);
    });
    return { byKey: nodesByKey, childrenByKey: children, roots: rootNodes };
  }, [data]);

  // the data can be regenerated while editing the indicator, drop an obsolete path
  useEffect(() => {
    if (path.some((key) => !byKey[key])) {
      setPath(path.filter((key) => byKey[key]));
    }
  }, [byKey]);

  const breadcrumb = path.map((key) => byKey[key]).filter((node) => node);
  const current = breadcrumb[breadcrumb.length - 1] || null;

  const level = useMemo(() => {
    const nodes = current ? (childrenByKey[keyOf(current)] || []) : roots;
    const slices = nodes.map((node) => ({ ...node }));
    // the values attached to the current element itself are kept as a slice,
    // otherwise the sum of the displayed slices would be lower than its own value
    if (current && current.ownValue > 0 && nodes.length > 0) {
      slices.push({
        ...current,
        name: t('graph.hierarchical_donut.own', { name: current.name }),
        value: current.ownValue,
        color: defaultColor,
        hasChildren: false,
        isOwnValue: true,
      });
    }
    return slices.sort((a, b) => (b.value - a.value));
  }, [current, childrenByKey, roots, t]);

  const total = level.reduce((acc, node) => acc + (node.value || 0), 0);
  const unit = level[0]?.unit;
  const rootLabel = t('graph.hierarchical_donut.root');

  // a node is clickable as long as its children are part of the displayed data
  const isClickable = (node) => !!node
    && !node.isOwnValue
    && childrenByKey[keyOf(node)]?.length > 0;

  const hoveredNode = hoveredIndex === null ? null : level[hoveredIndex];
  // recharts does not forward every presentation prop of a Cell, the cursor of the
  // hovered slice is also set on the container and inherited by the chart
  const cursor = isClickable(hoveredNode) ? 'pointer' : 'default';

  const drillDown = (node) => {
    if (!isClickable(node)) return;
    // the slices of the new level are not hovered until the mouse moves again
    setHoveredIndex(null);
    setPath([...path, keyOf(node)]);
  };

  const renderCustomizedLabel = (label) => {
    const {
      cx, cy, midAngle, innerRadius, outerRadius, name, value,
    } = label;
    const RADIAN = Math.PI / 180;
    const radius = 25 + innerRadius + (outerRadius - innerRadius);
    // eslint-disable-next-line
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    // eslint-disable-next-line
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#666666" fontSize="14px" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {name}
        {value && ` (${formatValue(value, label?.unit)})`}
      </text>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.graph} style={{ cursor }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              data={level}
              cx="50%"
              cy="50%"
              innerRadius={'50%'}
              outerRadius={'85%'}
              label={isLabel ? renderCustomizedLabel : null}
              isAnimationActive={false}
              onClick={(entry, index) => drillDown(level[index] || entry?.payload || entry)}
              onMouseEnter={(entry, index) => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              fill="#82ca9d"
            >
              {level.map((node, index) => (
                <Cell
                  key={`cell-${keyOf(node)}-${index}`}
                  fill={node.color}
                  stroke="#FFFFFF"
                  cursor={isClickable(node) ? 'pointer' : 'default'}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.center}>
          <p className={styles.label}>{current ? current.name : rootLabel}</p>
          {total > 0 && <p className={styles.value}>{formatValue(total, unit)}</p>}
        </div>
      </div>
      {/* the row is always there, so the chart area never overlaps the path */}
      <div className={styles.breadcrumb}>
        {current && (
          <div className={styles.path}>
            <button
              type="button"
              className={styles.home}
              title={rootLabel}
              onClick={() => setPath([])}
            >
              <IoHome />
            </button>
            {breadcrumb.map((node, i) => (
              <button
                key={keyOf(node)}
                type="button"
                className={node === current ? styles.active : ''}
                onClick={() => setPath(path.slice(0, i + 1))}
              >
                <IoChevronBack />
                {node.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
